// ============================================================
// PERRY — THE TREASURE
// The thing in his paws. Built to the gorgeous-model recipe like
// everything else in this title: NO boxes, no external assets,
// no .glb — tapered cylinders, cones, torus arcs and hard-scaled
// spheres, with a BackSide ink contour so it reads as DRAWN and
// an emissive edge that glows for free (toneMapped={false}).
//
// It owns NO state and takes NO position props. It reads guardRef
// inside useFrame and writes its own transform. React hears
// nothing while the fight is happening.
//
// THE SWING PASS, 2026-07-27. This file used to run its OWN curve
// — a `sin(life * 1.6 * PI)` off GUARD_HOLD_MS — while PerryBody's
// arm ran a `dt * 24` lerp. Two unrelated animations on two
// unrelated clocks, bolted to the same paw. That is most of why an
// answer read as mush: the blade peaked at a moment the arm knew
// nothing about. Both now call `swingEnvelope`, so the haft and the
// limb carrying it are the same motion by construction, and a
// retune in one place retunes the whole parry.
//
// A Treasure is EQUIPMENT belonging to one Being — the Empire's
// word for it, same drawer as the Gauntlets of A'zka and the
// Sword of Shadows. `shape.head` selects which one gets built.
// Adding a new one is one case in one switch and one entry in
// perryData.TREASURES. That is deliberate: there will be many,
// and some of them will be extremely stupid.
// ============================================================

import { useRef, useMemo } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { swingEnvelope, SWING } from "./qteEngine";

const INK = "#0c0806";
const HEAD_SCALE = 1.45;

// ── Grain — one cached 128² strip per haft tint ──────────────
const GRAIN = new Map();
function haftTexture(tint) {
  if (GRAIN.has(tint)) return GRAIN.get(tint);
  const c = document.createElement("canvas");
  c.width = 32; c.height = 128;
  const g = c.getContext("2d");
  g.fillStyle = tint; g.fillRect(0, 0, 32, 128);
  for (let i = 0; i < 26; i++) {
    const y = (i * 37 % 128);
    g.globalAlpha = 0.10 + (i % 5) * 0.04;
    g.fillStyle = i % 3 ? "#000" : "#ffffff";
    g.fillRect(0, y, 32, 1 + (i % 3));
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  GRAIN.set(tint, t);
  return t;
}

// ── The ink shell — a contour on any child geometry ──────────
function Contour({ scale = 1.07, children }) {
  return (
    <mesh scale={scale}>
      {children}
      <meshBasicMaterial color={INK} side={THREE.BackSide} />
    </mesh>
  );
}

// ── THE HEADS ────────────────────────────────────────────────
// Each returns geometry sitting at the TOP of the haft (+Y),
// pointing +Y, so the mount is identical for every Treasure.
function Head({ kind, hot, tint, thick }) {
  const edge = (
    <meshStandardMaterial
      color={tint} roughness={0.42} metalness={0.55}
      emissive={hot} emissiveIntensity={0.5}
    />
  );

  if (kind === "ladle") {
    return (
      <group position={[0, 0.06, 0]}>
        <mesh castShadow rotation={[Math.PI, 0, 0]} scale={[1, 0.62, 1]}>
          <sphereGeometry args={[0.075, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color={tint} roughness={0.55} metalness={0.4} emissive={hot} emissiveIntensity={0.35} side={THREE.DoubleSide} />
        </mesh>
        <Contour scale={1.09}>
          <sphereGeometry args={[0.075, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2]} />
        </Contour>
      </group>
    );
  }

  if (kind === "cleaver") {
    return (
      <group position={[0, 0.10, 0]}>
        {/* the wedge — a 4-sided cylinder, hard-scaled. No boxes. */}
        <mesh castShadow rotation={[0, Math.PI / 4, 0]} scale={[1, 1, 0.22]}>
          <cylinderGeometry args={[0.085, 0.10, 0.20, 4]} />
          {edge}
        </mesh>
        <mesh rotation={[0, Math.PI / 4, 0]} scale={[1.07, 1.05, 0.30]}>
          <cylinderGeometry args={[0.085, 0.10, 0.20, 4]} />
          <meshBasicMaterial color={INK} side={THREE.BackSide} />
        </mesh>
        {/* the lit edge — glows for free */}
        <mesh position={[0.075, 0, 0]} scale={[1, 1, 0.4]}>
          <cylinderGeometry args={[0.006, 0.006, 0.2, 6]} />
          <meshBasicMaterial color={hot} toneMapped={false} />
        </mesh>
      </group>
    );
  }

  if (kind === "hook") {
    return (
      <group position={[0, 0.05, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <mesh castShadow>
          <torusGeometry args={[0.06, thick * 0.9, 8, 20, Math.PI * 1.35]} />
          {edge}
        </mesh>
        <mesh scale={1.1}>
          <torusGeometry args={[0.06, thick * 0.9, 8, 20, Math.PI * 1.35]} />
          <meshBasicMaterial color={INK} side={THREE.BackSide} />
        </mesh>
        <mesh position={[0.055, 0.05, 0]}>
          <sphereGeometry args={[thick * 1.2, 8, 8]} />
          <meshBasicMaterial color={hot} toneMapped={false} />
        </mesh>
      </group>
    );
  }

  if (kind === "needles") {
    return (
      <group>
        {[-1, 1].map((s) => (
          <group key={s} position={[s * 0.028, 0.11, 0]} rotation={[0, 0, s * 0.09]}>
            <mesh castShadow>
              <coneGeometry args={[thick * 1.6, 0.22, 8]} />
              {edge}
            </mesh>
            <mesh scale={1.09}>
              <coneGeometry args={[thick * 1.6, 0.22, 8]} />
              <meshBasicMaterial color={INK} side={THREE.BackSide} />
            </mesh>
            <mesh position={[0, 0.11, 0]}>
              <sphereGeometry args={[thick * 0.9, 8, 8]} />
              <meshBasicMaterial color={hot} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>
    );
  }

  if (kind === "bill") {
    return (
      <group position={[0, 0.09, 0]}>
        {/* a beaten duck's bill, taken off something that wore it */}
        <mesh castShadow scale={[1, 1, 0.34]} rotation={[0, 0, 0]}>
          <sphereGeometry args={[0.085, 14, 10]} />
          {edge}
        </mesh>
        <mesh castShadow position={[0, 0.10, 0]} scale={[1, 1, 0.3]}>
          <coneGeometry args={[0.075, 0.16, 12]} />
          {edge}
        </mesh>
        <mesh position={[0, 0.10, 0]} scale={[1.08, 1.05, 0.36]}>
          <coneGeometry args={[0.075, 0.16, 12]} />
          <meshBasicMaterial color={INK} side={THREE.BackSide} />
        </mesh>
        <mesh position={[0, 0.02, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.055, 0.006, 6, 18]} />
          <meshBasicMaterial color={hot} toneMapped={false} />
        </mesh>
      </group>
    );
  }

  // "switch" — the reed he walked out of Reedbrook holding
  return (
    <group position={[0, 0.07, 0]}>
      <mesh castShadow>
        <coneGeometry args={[thick * 1.3, 0.18, 8]} />
        <meshStandardMaterial color={tint} roughness={0.8} emissive={hot} emissiveIntensity={0.25} />
      </mesh>
      <mesh scale={1.09}>
        <coneGeometry args={[thick * 1.3, 0.18, 8]} />
        <meshBasicMaterial color={INK} side={THREE.BackSide} />
      </mesh>
      <mesh position={[0, 0.10, 0]}>
        <sphereGeometry args={[thick * 1.1, 8, 8]} />
        <meshBasicMaterial color={hot} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ── THE TREASURE ─────────────────────────────────────────────
// guardRef: { current: { key, at, grade } } — set by the director
// on every press. The blade's whole performance is derived from
// how long ago that press was, so it is free and never re-renders.
export default function TreasureBody({ treasure, finish, guardRef, scale = 1 }) {
  const swing = useRef();
  const trail = useRef();
  const glowRef = useRef();
  const env = useMemo(() => ({}), []);   // written in place, never reallocated

  const shape = treasure?.shape || { haft: 0.5, thick: 0.014, head: "switch", guard: 0, tint: "#c8a066", hot: "#ffd08a" };
  const tint = finish?.tint || shape.tint;
  const hot = finish?.hot || shape.hot;
  const trailC = finish?.trail || hot;
  const grain = useMemo(() => haftTexture(shape.tint), [shape.tint]);

  useFrame((state) => {
    const g = guardRef?.current;
    const t = performance.now();
    const age = g && g.key ? t - g.at : 999999;
    const E = swingEnvelope(age, g?.grade || null, env);

    // The blade's own contribution, ON TOP of the aim the arm rig
    // is already giving it. The rig points it; this is the wrist.
    if (swing.current) {
      // cocked back through the wind-up, whipped through the swing,
      // kicked on contact, ringing after it.
      swing.current.rotation.x = -(E.reach - 1) * 0.42 + E.imp * 0.26;
      swing.current.rotation.z = E.ring * 0.14 - E.broke * 0.5;
      swing.current.position.y = E.bow * 0.05 - E.imp * 0.02;
      swing.current.scale.setScalar(1 + E.imp * 0.10);
    }

    // ── THE TRAIL, RETIMED ──
    // It used to burn brightest AFTER the answer and fade over the
    // hold, which is backwards: a swing ribbon belongs to the part
    // of the motion that is actually moving. `bow` peaks at the
    // exact middle of the travel — peak blade speed — so the ribbon
    // now lives where the speed is, then flashes once on contact.
    if (trail.current) {
      const m = trail.current.material;
      const speed = E.bow / SWING.BOW;                 // 0 → 1 → 0
      const flash = E.imp * (E.grade === "PERFECT" || E.grade === "FRACTURE" ? 1
                           : E.grade === "MISS" ? 0.12 : 0.45);
      if (m) m.opacity = Math.min(1, speed * 0.75 + flash * 0.6);
      trail.current.scale.set(0.7 + speed * 0.9, 0.5 + speed * 1.5, 1);
      trail.current.rotation.z = -E.bow * 1.1;
    }
    // ── THE HALO, RETUNED 2026-07-27 ──
    // It used to sit at a permanent 0.35 opacity, which on a 0.42
    // haft at 0.92 scale meant the single brightest thing on
    // Perry's body was an unattached bead hanging by his ear. That
    // bead was reading as the weapon, and the actual Treasure was
    // reading as nothing. It now sleeps at almost zero and BLOOMS
    // on an answer — light means something happened, not that an
    // object exists.
    // It now BLOOMS ON CONTACT rather than across the whole hold —
    // light means something just happened, not that an object
    // exists. A MISS barely lights at all, which is its own tell.
    if (glowRef.current?.material) {
      const idle = 0.06 + Math.sin(state.clock.elapsedTime * 2.1) * 0.03;
      const bloom = E.imp * (E.grade === "MISS" ? 0.25 : 1);
      glowRef.current.material.opacity = Math.max(idle, bloom * 0.95);
      glowRef.current.scale.setScalar(0.55 + bloom * 1.05 + E.bow * 0.3);
    }
  });

  return (
    <group scale={scale}>
      <group ref={swing}>
        {/* ── the haft ── */}
        <mesh castShadow position={[0, shape.haft / 2, 0]}>
          <cylinderGeometry args={[shape.thick * 0.82, shape.thick, shape.haft, 8]} />
          <meshStandardMaterial map={grain} color={tint} roughness={0.86} />
        </mesh>
        <mesh position={[0, shape.haft / 2, 0]} scale={[1.18, 1.02, 1.18]}>
          <cylinderGeometry args={[shape.thick * 0.82, shape.thick, shape.haft, 8]} />
          <meshBasicMaterial color={INK} side={THREE.BackSide} />
        </mesh>

        {/* ── a wrapped grip, because a beb has small paws ── */}
        <mesh position={[0, shape.haft * 0.18, 0]}>
          <cylinderGeometry args={[shape.thick * 1.5, shape.thick * 1.5, shape.haft * 0.22, 8]} />
          <meshStandardMaterial color="#2a1c14" roughness={1} />
        </mesh>

        {/* ── the crossguard, if this one has any pretensions ── */}
        {shape.guard > 0 && (
          <mesh castShadow position={[0, shape.haft * 0.92, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[shape.thick * 0.9, shape.thick * 0.9, 0.075 * shape.guard, 6]} />
            <meshStandardMaterial color={tint} roughness={0.4} metalness={0.6} emissive={hot} emissiveIntensity={0.3} />
          </mesh>
        )}

        {/* ── the business end ── */}
        {/* HEAD_SCALE: a beb wielding something slightly too big
            for him is the correct silhouette for this game, and a
            head you can identify across a fight mark is worth more
            than anatomical restraint. */}
        <group position={[0, shape.haft, 0]} scale={HEAD_SCALE}>
          <Head kind={shape.head} hot={hot} tint={tint} thick={shape.thick} />
        </group>

        {/* ── the lit edge halo — zero lights spent ── */}
        <mesh ref={glowRef} position={[0, shape.haft + 0.07, 0]}>
          <sphereGeometry args={[0.055, 10, 8]} />
          <meshBasicMaterial color={hot} transparent opacity={0.35} depthWrite={false} toneMapped={false} />
        </mesh>

        {/* ── the swing ribbon ── */}
        <mesh ref={trail} position={[0, shape.haft * 0.75, 0.02]} rotation={[0.2, 0, 0]}>
          <planeGeometry args={[0.34, 0.5]} />
          <meshBasicMaterial
            color={trailC} transparent opacity={0} depthWrite={false}
            side={THREE.DoubleSide} blending={THREE.AdditiveBlending} toneMapped={false}
          />
        </mesh>
      </group>
    </group>
  );
}
